import { Basic } from "../basic.ts";
import { Inputs } from "../../lib/inputs.js";
import { DataTypes } from "generated";
import { getFormattedValue, unprocessScaleType } from "../../lib/utils.js";
import { LegendMenu } from "./menu/menu.js";
import { isNullOrUndefined } from "../../lib/utils.js";
import {styleTabulator} from '../../styles/styleTabulator.js'
import i18next from "i18next";
import styles from './legend.css'

const eyeIcon = `
    <svg width="19" height="13" viewBox="0 0 19 13" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.5 9.48336C10.522 9.48336 11.3892 9.15168 12.1017 8.48833C12.8142 7.82498 13.1705 7.01757 13.1705 6.0661C13.1705 5.11463 12.8142 4.30722 12.1017 3.64387C11.3892 2.98052 10.522 2.64884 9.5 2.64884C8.47803 2.64884 7.6108 2.98052 6.8983 3.64387C6.1858 4.30722 5.82955 5.11463 5.82955 6.0661C5.82955 7.01757 6.1858 7.82498 6.8983 8.48833C7.6108 9.15168 8.47803 9.48336 9.5 9.48336ZM9.5 8.31747C8.82349 8.31747 8.25133 8.09971 7.78352 7.66417C7.31572 7.22864 7.08182 6.69595 7.08182 6.0661C7.08182 5.43625 7.31572 4.90356 7.78352 4.46803C8.25133 4.0325 8.82349 3.81473 9.5 3.81473C10.1765 3.81473 10.7487 4.0325 11.2165 4.46803C11.6843 4.90356 11.9182 5.43625 11.9182 6.0661C11.9182 6.69595 11.6843 7.22864 11.2165 7.66417C10.7487 8.09971 10.1765 8.31747 9.5 8.31747ZM9.5 12.0966C7.39848 12.0966 5.49849 11.5404 3.8 10.4281C2.10152 9.31585 0.834848 7.86184 0 6.0661C0.834848 4.27037 2.10152 2.81635 3.8 1.70407C5.49849 0.591787 7.39848 0.0356445 9.5 0.0356445C11.6015 0.0356445 13.5015 0.591787 15.2 1.70407C16.8985 2.81635 18.1652 4.27037 19 6.0661C18.1652 7.86184 16.8985 9.31585 15.2 10.4281C13.5015 11.5404 11.6015 12.0966 9.5 12.0966ZM9.5 10.8905C11.2417 10.8905 12.843 10.4516 14.304 9.57382C15.765 8.69605 16.8769 7.52681 17.6398 6.0661C16.8769 4.60539 15.765 3.43615 14.304 2.55839C12.843 1.68062 11.2417 1.24174 9.5 1.24174C7.75833 1.24174 6.15701 1.68062 4.69602 2.55839C3.23504 3.43615 2.11591 4.60539 1.33864 6.0661C2.11591 7.52681 3.23504 8.69605 4.69602 9.57382C6.15701 10.4516 7.75833 10.8905 9.5 10.8905Z"/>
    </svg>
`

const popupCss = `
    .PopupDiv {
        background: #fff;
    }
    .up-down {
        background-color: #fff !important;
        border: none !important;
    }
`

const numericCss = `
    #decr {
        border: none !important;
        margin-right: 10px;
    }
    div:nth-child(2) {
        box-sizing: border-box;
        border: 1px solid;
    }
    #incr {
        border: none !important;
        margin-left: 10px;
    }

    :host {
        border: none !important;
        background-color: #fff !important;
    }
`

const checkboxCss = `
    div:nth-child(1) {
        background-color: #fff !important;
    }
`

const observedAttributes = [
    "trend",
    "gridtype",
    "showpen",
    "showname",
    "showsource",
    "showgroup",
    "showvalue",
    "showavarage",
    "showcomment",
    "showunit",
    "showlastvaluetime",
    "showquality",
    "showmin",
    "showmax",
    "shownau",
    "shownpu",
    "showvpu",
    "showvau",
    "showstartvalue",
    "showminvalue",
    "showmaxvalue",
    "showlastvalue",
    "columnwidth",
    "fixcolumnwidth",
    "columnfontsize",
    "columnalignhorizontal",
    "columnalignvertical",
    "columnfontcolor",
    "setcolumnbolt",
    "setcolumnitalic",
    "setcolumnunderline",
]

/**
 * @class MSLegend
 * @extends Basic
 * @classdesc Легенда для Тренда
 * */
export class MSLegend extends Basic {
    static get observedAttributes() {
        return super.observedAttributes.concat(observedAttributes);
    }
    constructor() {
        super();
        this.style.overflow = "visible";
        const shadowRoot = this.attachShadow({ mode: "open" });
        shadowRoot.innerHTML = `<style>
            :host {
                user-select: none;
            }
            [class^="hmi-j-"], [class*=" hmi-j-"] {
                /* use !important to prevent issues with browser extensions that change fonts */
                font-family: 'hmi' !important;
                speak: none;
                font-style: normal;
                font-weight: normal;
                font-variant: normal;
                text-transform: none;
                line-height: 1;
                font-size: 14px;
                /* Better Font Rendering =========== */
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
            }
            .hmi-j-chk_empt:before {
                content: "\\e907";
            }

            .hmi-j-chk_sel:before {
                content: "\\e908";
            }
            .box-p {
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                top: 0;
                bottom: 0;
                left: 10;
                right: 10;
            }
            .box-p-i {
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
            }

        </style>
        <style>
            ${styles}
            ${styleTabulator}
        </style>
        <style id="rowstyle"></style>
        <div class="legend-root">
            <div class="legend-container">
                <div id="legend-inner"></div><div id="inbox"></div>
            </div>
            <div class="menu-root" id="menu-root"></div>
        </div>`;
        this.rootContainer = shadowRoot.querySelector(".legend-root");
        this._main = shadowRoot.querySelector("#legend-inner");
        this._in = shadowRoot.querySelector("#inbox");
        this._rowstyle = shadowRoot.querySelector("#rowstyle")
        this._gridtype = 0;
        this.columns = [
            {
                id: 0,
                title: `<div class="header-visible-container"><span id="shAll" class="header-visible">${eyeIcon}</span><div>`,
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_visible');
                },
                field: "visible",
                frozen: true,
                minWidth: 50,
                formatter: (cell, formatterParams) => {
                    return cell.getValue()
                        ? `<div class="visible-wrap visible">${eyeIcon}</div>`
                        : `<div class="visible-wrap">${eyeIcon}</div>`;
                },
                hozAlign: "center",
                cellClick: (e, cell) => {
                    const pen = this._trendControl.pens[cell.getData().id]
                    if (this._trendControl.legend){
                        this._trendControl.legend.onCheckbox(pen.idx)()
                    } else {
                        pen.isvisible = !pen.isvisible
                    }
                    this._trendControl.saveState()
                },
                headerClick: (e, col) => {
                    if (e.target.tagName !== "DIV") {
                        e.stopImmediatePropagation();
                        const sel = this._shAll.classList.contains("visible");
                        for (let index = 0; index < this._trendControl.pens.length; index++) {
                            const element = this._trendControl.pens[index];
                            element.isvisible = !sel;
                        }
                        if (this._trendControl.legend) this._trendControl.legend.render()
                    }
                },
            },
            {
                title: i18next.t('legend_title_pen'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_pen');
                },
                field: "graph",
                frozen: true,
                formatter: this._genGraph,
                variableHeight: false,
                id: 1,
            },
            {
                title: i18next.t('legend_title_name'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_name');
                },
                field: "name",
                frozen: true,
                id: 2,
            },
            {
                title: i18next.t('legend_title_source'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_source');
                },
                field: "source",
                frozen: true,
                id: 3,
            },
            {
                title: i18next.t('legend_title_group'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_group');
                },
                field: "group",
                formatter: (cell) => {
                    const source = cell.getValue()
                    const { title, isMain } = JSON.parse(source)
                    return `<div class="cell-group-wrap"><div>${title}</div> ${title && isMain ? '<span class="main-pen">*</span></div>' : ''}`
                },
                id: 28,
            },
            {
                title: i18next.t('legend_title_units'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_units');
                },
                field: "units",
                id: 4,
                hozAlign: 'center'
            },
            {
                title: i18next.t('legend_title_value'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_value');
                },
                field: "value",
                id: 5,
            },
            {
                title: i18next.t('legend_title_avarage'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_avarage');
                },
                field: "avarage",
                id: 20,
            },
            {
                title: i18next.t('legend_title_comment'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_comment');
                },
                field: "comment",
                id: 18,
                hozAlign: 'center'
            },
            {
                title: i18next.t('legend_title_lasttime'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_lasttime');
                },
                field: "lasttime",
                id: 6,
            },
            {
                title: i18next.t('legend_title_isbad'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_isbad');
                },
                field: "isbad",
                id: 7
            },
            // {
            //     title: "ш.вид", field: "showaxis", formatter: (cell, formatterParams) => {
            //         return cell.getValue() ? '<span class="hmi-j-chk_sel"></span>' : '<span class="hmi-j-chk_empt"></span>';
            //     }, align: "center", cellClick: (e, cell) => {
            //         this._trendControl.pens[cell.getData().id].showaxis = !cell.getValue();
            //     }
            // },
            {
                title: i18next.t('legend_title_scalemin'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_scalemin');
                },
                field: "scalemin",
                formatter: (cell) => {
                    const source = cell.getValue()
                    const { miny, title, isMain, autoscale } = JSON.parse(source)
                    return this.formatterScale(miny, title, autoscale, isMain)

                },
                id: 8,
            },
            {
                title: i18next.t('legend_title_scalemax'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_scalemax');
                },
                field: "scalemax",
                formatter: (cell) => {
                    const source = cell.getValue()
                    const { maxy, title, isMain, autoscale } = JSON.parse(source)
                    return this.formatterScale(maxy, title, autoscale, isMain)
                },
                id: 9,
            },
            {
                title: i18next.t('legend_title_lolo'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_lolo');
                },
                field: "lolo",
                id: 10,
            },
            {
                title: i18next.t('legend_title_lo'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_lo');
                },
                field: "lo",
                id: 11,
            },
            {
                title: i18next.t('legend_title_hi'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_hi');
                },
                field: "hi",
                id: 12,
            },
            {
                title: i18next.t('legend_title_hihi'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_hihi');
                },
                field: "hihi",
                id: 13,
            },
            {
                title: i18next.t('legend_title_start'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_start');
                },
                field: "start",
                id: 14,
            },
            {
                title: i18next.t('legend_title_min'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_min');
                },
                field: "min",
                id: 15,
            },
            {
                title: i18next.t('legend_title_max'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_max');
                },
                field: "max",
                id: 16,
            },
            {
                title: i18next.t('legend_title_end'),
                headerTooltip: () => {
                    return i18next.t('legend_tooltip_end');
                },
                field: "end",
                id: 17,
            },
        ];
        this._loadState();

        const fnSize = document.createElement("span");
        fnSize.style.fontSize = "14px";
        fnSize.style.fontWeight = "bold";
        this.columnalignhorizontal = "1"
        document.body.appendChild(fnSize);

        for (let i = 0; i < this.columns.length; i++) {
            if (typeof this.columns[i].width !== "undefined") continue;
            const regex = /<[a-zA-Z\/][^>]*>/g;
            fnSize.innerText = this.columns[i].title.replace(regex, "");
            const offset =
                fnSize.innerText.length < this.columns[i].title.length
                    ? 60
                    : 40;
            this.columns[i].width =
                fnSize.getBoundingClientRect().width + offset;
        }
        fnSize.remove();

        this.__reciver = this._reciver.bind(this);
        this.__clearState = this._clearState.bind(this);
        this._tabledata = [];
    }

    initMenu() {
        const container = this.shadowRoot.getElementById('menu-root')
        this.menu = new LegendMenu(container, this)
    }

    updateShAll(show) {
        if (!this._shAll) return
        if (show) {
            this._shAll.classList.add("visible");
        } else {
            this._shAll.classList.remove("visible");
        }
    }
    set height(value) {
        super.height = value;
        if (this.table) {
            this.table.setHeight(value);
        }
    }

    get gridtype() {
        return this._gridtype;
    }
    set gridtype(v) {
        this._gridtype = Number(v);
        this.genRowStyle();
    }

    formatterScale(value, title, autoscale, isMain){
        if(autoscale){
            return `<div class="cell-group-wrap"><div class="cell-scale-text disable-text">${i18next.t('legend_autoscaling_on')}</div></div>`
        } else {
            return `<div class="cell-group-wrap"><div class="cell-scale-text ${title && !isMain ? "disable-text" : ""}">${value}</div>${title && isMain ? '<span class="main-pen">*</span>' : ''}</div>`
        }
    }

    genRowStyle() {
        this._rowstyle.innerHTML = `
        .tabulator-row {
          ${this._gridtype === 1 || this._gridtype === 2
                ? `border-bottom: 1px solid gray !important;;`
                : `border-bottom: 1px none !important;`
            };
        }
        .tabulator-cell {
          ${this._gridtype === 0 || this._gridtype === 2
                ? `border-right: 1px solid gray !important;`
                : `border-right: 1px none !important;`
            };
        }`;
    }

    _clearState(){
        this.originPath = this.getOriginPath();
        this.saveItemState({ colState: undefined });
        if(this.columnwidth){
            this.table.getColumns().forEach(col => col._column.setWidth(this.columnwidth))
        } else {
            this.table.getColumns().forEach(col => col._column.setWidth(0))
            this.table.redraw(true)
        }
    }
    get trend() {
        return this._trend;
    }
    set trend(value) {
        this._trend = value;
        if (typeof value !== "object") {
            let ms = this.parentElement;
            while (ms.nodeName !== "MS-WINDOW") {
                ms = ms.parentElement;
            }
            if (value) {
                if (this._trendControl){
                    this._trendControl.removeObserver(this.__reciver);
                    this._trendControl.removeObserverClear(this.__clearState);
                }

                this._trendControl = ms.querySelector(`[id="${value}"]`);
                if (this._trendControl) {
                    this._trendControl.addObserver(this.__reciver);
                    this._trendControl.addObserverClear(this.__clearState);
                } else {
                    $ns.add({
                        type: "error",
                        time: new Date().toLocaleString(),
                        title: "wrong trend ID",
                        text: `legend ID:${this.id} set wrong trend ID:${value}`,
                    });
                }
            }
        } else {
            this._trendControl = value;
        }
        if(this._trendControl) this._trendControl.tableLegend = this
    }

    _reciver() {
        if (!this.table) return;
        const data = this._trendControl.pens;
        if (!this.updateloc) {
            this.updateloc = true;
            requestAnimationFrame(() => {
                if (!this.table) return;
                const options = {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    second: "numeric",
                };
                if (data.length < this.table.getDataCount()) {
                    const offset = this.table.getDataCount() - data.length;
                    for (
                        let i = this.table.getDataCount() - offset;
                        i < this.table.getDataCount();
                        i++
                    ) {
                        this.table.deleteRow(this.table.getDataCount() - 1);
                        i--;
                    }
                }
                var shCalc = false;
                for (let i = 0; i < data.length; i++) {
                    const el = data[i];
                    if (el.isvisible) shCalc = true;
                    let time = "",
                        value = "",
                        isbad = 0,
                        lolo = "",
                        lo = "",
                        hi = "",
                        hihi = "",
                        yFormat = "";
                    let decor,
                        type = "";
                    if (el.serie.type === "custom") {
                        if (el.isEnum) {
                            let index;
                            if (el.lastValue) {
                                index = el.lastValue[1];
                                if (el.subType.Indexes) {
                                    index = el.subType.Indexes.findIndex(
                                        (eli) => eli == el.lastValue[1]
                                    );
                                }
                            }
                            const color =
                                el.lastValue && el.lastValue[1]
                                    ? el.subType.Colors[index]
                                    : el.color;
                            decor = `${color}|solid|4`;
                            type = "custom";
                        } else {
                            decor = `${el.color}|solid|4`;
                            type = "custom";
                        }
                    } else {
                        decor = `${el.color}|${el.linestyle}|${el.thickness}|${el.serie.symbol}|${el.serie.symbolSize}`;
                    }

                    if (el.lastValue) {
                        if (el.isEnum) {
                            let index;
                            if (el.lastValue) {
                                index = el.lastValue[1];
                                if (el.subType.Indexes) {
                                    index = el.subType.Indexes.findIndex(
                                        (eli) => eli == el.lastValue[1]
                                    );
                                }
                            }
                            value = el.subType.DisplayValues[index];
                        } else {
                            switch (typeof el.lastValue[1]) {
                                case "boolean":
                                    yFormat = "";
                                    break;
                                default:
                                    yFormat = el.yformat;
                            }
                            if(!isNullOrUndefined(el.lastValue[1])){
                                value = getFormattedValue(
                                    yFormat,
                                    unprocessScaleType(el.lastValue[1], el.logscale)
                                );
                            }
                            time = new Date(el.lastValue[0]).toLocaleDateString(
                                "ru",
                                options
                            );
                            isbad = el.lastValue[2] || 0;
                        }
                    }
                    if (el.penlimits) {
                        lolo = el.penlimits.lolo
                            ? el.penlimits.lolo.datasource
                            : "";
                        lo = el.penlimits.lo ? el.penlimits.lo.datasource : "";
                        hi = el.penlimits.hi ? el.penlimits.hi.datasource : "";
                        hihi = el.penlimits.hihi
                            ? el.penlimits.hihi.datasource
                            : "";
                    }

                    const avarage = typeof el?.avarage === 'number' ? getFormattedValue(
                        yFormat,
                        unprocessScaleType(el.avarage, el.logscale)
                    ) : el.avarage
                    const groupName = el.groupname
                    const isMain = el.mainpengroup
                    const autoscale = el.autoscale
                    this.table.updateOrAddRow(i, {
                        avarage: avarage,
                        id: i,
                        visible: el.isvisible,
                        group: JSON.stringify({ title: groupName || "", isMain }),
                        graph: decor,
                        _type: type,
                        name: el.name,
                        source: el.source,
                        units: el.unit,
                        value: value,
                        lasttime: time,
                        isbad: isbad,
                        comment: el.comment || "",
                        // showaxis: el.showaxis,
                        scalemin: JSON.stringify({ miny: el.miny === 0 ? el.miny : unprocessScaleType(el.miny, el.logscale), title: groupName || "", isMain, autoscale, yformat: el.yformat }),
                        scalemax:  JSON.stringify({ maxy: unprocessScaleType(el.maxy, el.logscale), title: groupName || "", isMain, autoscale, yformat: el.yformat }),
                        lolo: lolo,
                        lo: lo,
                        hi: hi,
                        hihi: hihi,
                        min:
                            el.minmax &&
                                el.minmax[0] !== "" &&
                                el.minmax[0] !== null
                                ? getFormattedValue(
                                    yFormat,
                                    unprocessScaleType(
                                        el.minmax[0].toString()
                                    )
                                )
                                : "",
                        max:
                            el.minmax &&
                                el.minmax[1] !== "" &&
                                el.minmax[1] !== null
                                ? getFormattedValue(
                                    yFormat,
                                    unprocessScaleType(
                                        el.minmax[1].toString()
                                    )
                                )
                                : "",
                        start:
                            el.minmax &&
                                el.minmax[2] !== "" &&
                                el.minmax[2] !== null
                                ? getFormattedValue(
                                    yFormat,
                                    unprocessScaleType(
                                        el.minmax[2].toString()
                                    )
                                )
                                : "",
                        end:
                            el.minmax &&
                                el.minmax[3] !== "" &&
                                el.minmax[3] !== null
                                ? getFormattedValue(
                                    yFormat,
                                    unprocessScaleType(
                                        el.minmax[3].toString()
                                    )
                                )
                                : "",
                    });
                    if (el.selected && this._selectedIndex !== i) {
                        this._oldSelectedIndex = this._selectedIndex;
                        this._selectedIndex = i;
                        this.table.selectRow(i);
                    }
                }
                this.updateShAll(shCalc);
                if (this._oldSelectedIndex !== undefined) {
                    this.table.deselectRow(this._oldSelectedIndex);
                }
                if(this._trendControl.currentPen === undefined){
                    this._trendControl.pens.forEach((pen, i) => {
                        this.table.deselectRow(i);
                    })
                    this._selectedIndex = null
                }
                this.updateloc = false;
            });
        }
    }

    _genGraph(cell) {
        const param = cell.getValue().split("|");
        let inner = "";

        if (param[4] > 0) {
            const paddingRight = 20;
            const arrowWidth = 6;
            const figureOffset = `calc(50% - ${(arrowWidth + paddingRight) / 2}px)`;

            switch (param[3]) {
                case "rect":
                    inner += `<div style="background-color:${param[0]};
                        width:${param[4]}px; height:${param[4]}px;
                        position:absolute;
                        left:${figureOffset};
                        top:50%;
                        transform:translate(-50%, -50%);">
                    </div>`;
                    break;

                case "circle":
                    inner += `<div style="border-radius:50%; background-color:${param[0]};
                        width:${param[4]}px; height:${param[4]}px;
                        position:absolute;
                        left:${figureOffset};
                        top:50%;
                        transform:translate(-50%, -50%);">
                    </div>`;
                    break;

                case "triangle":
                    inner += `<div style="
                        width:0; height:0;
                        border-style:solid;
                        border-width:0 ${param[4] / 2}px ${param[4]}px ${param[4] / 2}px;
                        border-color:transparent transparent ${param[0]} transparent;
                        position:absolute;
                        left:${figureOffset};
                        top:50%;
                        transform:translate(-50%, -50%);">
                    </div>`;
                    break;

                case "diamond":
                    inner += `<div style="background-color:${param[0]};
                        width:${param[4]}px; height:${param[4]}px;
                        position:absolute;
                        left:${figureOffset};
                        top:50%;
                        transform:translate(-50%, -50%) rotate(-45deg);">
                    </div>`;
                    break;

                case "none":
                    break;

                default:
                    inner += `<div style="
                        width:${param[4]}px; height:${param[4]}px;
                        position:absolute;
                        left:${figureOffset};
                        top:50%;
                        transform:translate(-50%, -50%) rotate(-45deg);
                        background:
                            linear-gradient(to bottom, transparent 35%, ${param[0]} 35%, ${param[0]} 65%, transparent 65%),
                            linear-gradient(to right, transparent 35%, ${param[0]} 35%, ${param[0]} 65%, transparent 65%);
                    "></div>`;
                    break;
            }
        }
        inner += `
            <div class="line" style="border-top:${param[2]}px ${param[1]} ${param[0]};"></div>
            <div class="icon-arrow"></div>
        `;

        return `
            <div class="box-p">
                <div class="box-p-i flex-wrap" style="pointer-events:none; width:100%; position:relative; display:flex; align-items:center;">
                    ${inner}
                </div>
            </div>
        `;
    }

    set width(v) {
        super.width = v
        this.rootContainer.style.width = v + "px"
    }

    setScaleMax(e, row) {
        const type = row.getData()._type;
        if (type === "custom") return;
        if (
            $pm.hasPermissions &&
            !$pm.checkAllow($pm.getEntryFromElement(this, "TrendChangePens"))
        )
            return;
        const { maxy, title, isMain, autoscale, yformat} = JSON.parse(row.getData().scalemax)
        if (autoscale) return
        const { miny } = JSON.parse(row.getData().scalemin)
        const maxdecimalplaces = this.getMaxdecimalplaces(yformat)
        const inputNum = new Inputs(this._in);
        const html = `<style>${popupCss}</style>${(title && !isMain) ? `<div class="tooltip-wrap"><div style="width: 100%;text-align: center;color: #1E90FF;">${i18next.t('legend_scale_no_use')}</div><div class="tooltip">${i18next.t('legend_scale_no_use_tooltip')}</div></div>` : ''}
            <p style="text-align: center; width: 100%; margin: 7px 0;">Max</p>`
        inputNum.inputBoxNumber(
            null,
            html,
            maxy,
            (v) => {
                const value = Number(v);
                if (!isNaN(value) && maxy !== value) {
                    if ($pm.hasPermissions) {
                        const options = {
                            cancel: () => { },
                            customText: `${this.elname} Изменение шкалы пера ${this._trendControl.pens[row.getIndex()].name
                                }: ${this._trendControl.pens[row.getIndex()].miny
                                }-${value}`,
                        };
                        $pm.resolveControl(
                            this,
                            "TrendChangePens",
                            () => {
                                this._trendControl.pens[row.getIndex()].maxy =
                                    value;
                                this._trendControl.saveState();
                            },
                            options
                        );
                    } else {
                        this._trendControl.pens[row.getIndex()].maxy = value;
                        this._trendControl.saveState();
                    }
                    if(isMain){
                        this._trendControl.trendGroups.reinit();
                    }
                }
            },
            {
                toBody: true,
                alignToElement: true,
                element: e.target,
                showFocus: true,
                min: miny,
                max: Infinity,
                maxdecimalplaces,
                zIndex: 11000000,
            }
        );
        const numericElem = inputNum._PopupDiv.querySelector('ms-numericupdown')
        this.restyleNumericUpDown(numericElem)
    }

    setScaleMin(e, row) {
        const type = row.getData()._type;
        if (type === "custom") return;
        if (
            $pm.hasPermissions &&
            !$pm.checkAllow($pm.getEntryFromElement(this, "TrendChangePens"))
        )
            return;
        const { miny, title, isMain, autoscale, yformat} = JSON.parse(row.getData().scalemin)
        if (autoscale) return
        const { maxy } = JSON.parse(row.getData().scalemax)
        const maxdecimalplaces = this.getMaxdecimalplaces(yformat)
        const inputNum = new Inputs(this._in);
        const html = `<style>${popupCss}</style>${(title && !isMain) ? `<div class="tooltip-wrap"><div style="width: 100%;text-align: center;color: #1E90FF;">${i18next.t('legend_scale_no_use')}</div><div class="tooltip">${i18next.t('legend_scale_no_use_tooltip')}</div></div>` : ''}
        <p style="text-align: center; width: 100%; margin: 7px 0;">Min</p>`
        inputNum.inputBoxNumber(
            null,
            html,
            miny,
            (v) => {
                const value = Number(v);
                if (!isNaN(value) && miny !== value) {
                    if ($pm.hasPermissions) {
                        const options = {
                            cancel: () => { },
                            customText: `${this.elname} Изменение шкалы пера ${this._trendControl.pens[row.getIndex()].name
                                }: ${value}-${this._trendControl.pens[row.getIndex()].maxy
                                }`,
                        };
                        $pm.resolveControl(
                            this,
                            "TrendChangePens",
                            () => {
                                this._trendControl.pens[row.getIndex()].miny =
                                    value;
                                this._trendControl.saveState();
                            },
                            options
                        );
                    } else {
                        this._trendControl.pens[row.getIndex()].miny = value;
                        this._trendControl.saveState();
                    }
                    if(isMain){
                        this._trendControl.trendGroups.reinit();
                    }
                }
            },
            {
                toBody: true,
                alignToElement: true,
                element: e.target,
                showFocus: true,
                min: -Infinity,
                max: maxy,
                maxdecimalplaces,
                zIndex: 11000000,
            }
        );
        const numericElem = inputNum._PopupDiv.querySelector('ms-numericupdown')
        this.restyleNumericUpDown(numericElem)
    }

    getMaxdecimalplaces(format){
        const startsWithAllowedCharacter = ["f", "{f"].some(x =>
            format.startsWith(x)
        );
        if(startsWithAllowedCharacter){
            const cleanedformat = format.replace(/[{}]/g, '');
            return cleanedformat.match(/\d+/)[0];
        } else {
            return 3
        }
    }

    setGraph(e, row) {
        const inputNum = new Inputs(this._in);
        const type = row.getData()._type;
        let index = 0;
        const pcursor =
            window._enums.PointType[
            this._trendControl.pens[row.getIndex()].pointtype
            ];
        const PenPointType =
            type !== "custom"
                ? DataTypes.dataTypes["HMI.PenPointType"].DisplayValues.map(
                    (current) => {
                        return `<option value="${index}"${index++ === pcursor ? " selected" : ""
                            }>${current}</option>`;
                    }
                ).join()
                : "";
        let html = `<style>${popupCss}
            .PopupDiv {
                padding: 24px 26px;
            }
            .up-down {
                background-color: #fff !important;
                border: none !important;
            }
        </style>`;
        html +=
            '<div style="flex-direction:column;width:240px;margin-bottom:19px;">';
        if (type !== "custom") {
            html += `
            <div style="margin-top:1em;display:flex;">
                <span style="align-self: center; min-width:135px;">${i18next.t('legend_menu_type_graph_title')}</span>
                <select id="linejoin" style="width: 124px; height: 27px;">
                    <option value="false"${!this._trendControl.pens[row.getIndex()].linejoin
                    ? " selected"
                    : ""
                }>${i18next.t('legend_menu_type_graph_linear')}</option>
                    <option value="true"${!this._trendControl.pens[row.getIndex()].linejoin
                    ? ""
                    : " selected"
                }>${i18next.t('legend_menu_type_graph_stepped')}</option>
                </select>
            </div>
            <div style="margin-top:1em;display: flex;">
                <span style="align-self: center; min-width:135px;">${i18next.t('legend_menu_thickness_title')}</span>
                <ms-numericupdown class="up-down" height="27" width="124" texthorizontalalign="1" type="number" id="inp" size="40" value="${this._trendControl.pens[row.getIndex()].thickness
                }" incrementstep="1.0" style="position:relative; width: 180px; border: 1px solid;"></ms-numericupdown>
            </div>`;
        }
        if (this._trendControl.pens[row.getIndex()].isEnum) {
            for (const key in this._trendControl.pens[row.getIndex()].subType
                .Colors) {
                const color =
                    this._trendControl.pens[row.getIndex()].subType.Colors[key];
                const name =
                    this._trendControl.pens[row.getIndex()].subType
                        .DisplayValues[key];
                html += `
                <div style="margin-top:1em;display:flex;">
                    <span style="align-self: center; min-width:135px;">${name}</span>
                    <ms-colorpicker data-index="${key}" borderthickness="1" bordercolor="#131517" height="24" width="24" colorselect="${color}" style="position:relative; width:110px; height:20px;"></ms-colorpicker>
                </div>`;
            }
        } else {
            html += `
            <div style="margin-top:1em;display:flex;">
                <span style="align-self: center; min-width:135px;">${i18next.t('legend_menu_color_title')}</span>
                <ms-colorpicker borderthickness="1" bordercolor="#131517" height="24" width="24" colorselect="${this._trendControl.pens[row.getIndex()].color
                }" style="position:relative; width:110px; height:20px;"></ms-colorpicker>
            </div>`;
        }

        if (type !== "custom") {
            html += `
            <div style="margin-top:1em;display:flex;">
                <span style="align-self: center; min-width:135px;">${i18next.t('legend_menu_type_line_title')}</span>
                <select id="linestyle" style="width: 124px; height: 27px;">
                    <option value="0"${this._trendControl.pens[row.getIndex()].linestyle ===
                    "solid"
                    ? " selected"
                    : ""
                }>${i18next.t('legend_menu_type_line_line')}</option>
                    <option value="1"${this._trendControl.pens[row.getIndex()].linestyle ===
                    "dashed"
                    ? " selected"
                    : ""
                }>${i18next.t('legend_menu_type_line_dotted')}</option>
                    <option value="2"${this._trendControl.pens[row.getIndex()].linestyle ===
                    "dotted"
                    ? " selected"
                    : ""
                }>${i18next.t('legend_menu_type_line_points')}</option>
                </select>
            </div>
            <div style="margin-top:1em;display:flex;">
                <span style="align-self: center; min-width:135px;">${i18next.t('legend_menu_type_point_title')}</span>
                <select id="PenPointType" style="width: 124px; height: 27px;">
                    ${PenPointType}
                </select>
            </div>
            <div style="margin-top:1em;display: flex;">
                <span style="align-self: center; min-width:135px;">${i18next.t('legend_menu_point_size_title')}</span>
                <ms-numericupdown class="up-down" height="27" width="124" texthorizontalalign="1" type="number" id="pointsize" size="40" value="${this._trendControl.pens[row.getIndex()].pointsize
                }" incrementstep="1.0" style="position:relative; width: 180px; border: 1px solid;"></ms-numericupdown>
            </div>
            ${this._trendControl.yaxisperpen ?
            `<div style="margin-top:1em;display:flex;">
                <span style="align-self: center; min-width:135px;">${i18next.t('legend_menu_scale_visibility_title')}</span>
                <ms-checkbox id="showaxis" borderstyle="0" borderthickness="1" bordercolor="#8C8C8C" height="15" width="15" ischecked="${this._trendControl.pens[row.getIndex()].showaxis
                }" style="position:relative;"></ms-checkbox>
            </div>`
            : ""}
            <div style="margin-top:1em;display:flex;">
                <span style="align-self: center; min-width:135px;">${i18next.t('legend_menu_ddding_constants_title')}</span>
                <ms-checkbox id="drawconstant" borderstyle="0" borderthickness="1" bordercolor="#8C8C8C" height="15" width="15" ischecked="${this._trendControl.pens[row.getIndex()].drawconstant
                }" style="position:relative;"></ms-checkbox>
            </div>
            <div style="margin-top:1em;display:flex;">
                <span style="align-self: center; min-width:135px;">${i18next.t('legend_menu_autoscaling_title')}</span>
                <ms-checkbox id="autoscale" borderstyle="0" borderthickness="1" bordercolor="#8C8C8C" height="15" width="15" ischecked="${this._trendControl.pens[row.getIndex()].autoscale
                }" style="position:relative;"></ms-checkbox>
            </div>
        `;
        }
        html += "</div>";
        inputNum.inputBoxCustom(
            null,
            html,
            row.getData().scalemin,
            (v) => {
                const currentPen = this._trendControl.pens[row.getIndex()];

                if (currentPen.isEnum) {
                    const colorselect = v.querySelectorAll("ms-colorpicker");
                    colorselect.forEach((element) => {
                        currentPen.subType.Colors[element.dataset.index] =
                            element.colorselect;
                    });
                } else {
                    const colorselect =
                        v.querySelector("ms-colorpicker").colorselect;
                    currentPen.color = colorselect;
                    this._trendControl?.trendGroups?.reinit();
                }

                if (type !== "custom") {
                    const th = v.querySelector("ms-numericupdown").value;
                    currentPen.thickness = Number(th);
                    const linestyle = v.querySelector('[id="linestyle"]').value;
                    currentPen.linestyle = Number(linestyle);
                    const linejoin = v.querySelector('[id="linejoin"]').value;
                    currentPen.linejoin = this._toBool(linejoin);
                    const ppType = v.querySelector('[id="PenPointType"]').value;
                    currentPen.pointtype = Number(ppType);
                    const pointsize = v.querySelector('[id="pointsize"]').value;
                    currentPen.pointsize = Number(pointsize);
                    const showaxisEl = v.querySelector('[id="showaxis"]')
                    if(showaxisEl){
                    const showaxis = showaxisEl.ischecked;
                    currentPen.showaxis = this._toBool(showaxis);
                    }
                    const drawconstant = v.querySelector(
                        '[id="drawconstant"]'
                    ).ischecked;
                    currentPen.drawconstant = this._toBool(drawconstant);

                    const isAutoscaleChecked =
                        v.querySelector('[id="autoscale"]').ischecked;
                    currentPen.autoscale = this._toBool(isAutoscaleChecked);
                }
                this._trendControl.saveState();
                this._reciver();
            },
            {
                toBody: true,
                alignToElement: true,
                element: e.target,
                showFocus: true,
                zIndex: 11000000,
            }
        );
        const numElementsLIst = inputNum._PopupDiv.querySelectorAll('ms-numericupdown')
        for (let i = 0; i < numElementsLIst.length; i++) this.restyleNumericUpDown(numElementsLIst[i])
        const checkElementsLIst = inputNum._PopupDiv.querySelectorAll('ms-checkbox')
        for (let i = 0; i < checkElementsLIst.length; i++) {
            const style = document.createElement('style')
            style.innerHTML = checkboxCss
            checkElementsLIst[i].shadowRoot.appendChild(style)
        }
    }

    restyleNumericUpDown(elem) {
        const style = document.createElement('style')
        style.innerHTML = numericCss
        elem.shadowRoot.appendChild(style)
    }

    connectedCallback() {
        this.table = new Tabulator(this._main, {
            autoResize: true,
            layout: "fitData",
            height: "100%", // Высота таблицы
            layoutColumnsOnNewData: false,
            virtualDom: true,
            pagination: false,
            columns: this.columns,
            // movableColumns: true,
            headerSortTristate: true,
            resizableRows:true,
            columnDefaults:{
                resizable:true,
            },
        });
        this.table.on("columnResized", (column) => {
            this._saveState();
            if (!column.getDefinition().frozen) return;
            let width = 0;
            for (const col of column.getTable().getColumns()) {
                if (
                    col.getField() !== column.getField() &&
                    col.getDefinition().frozen
                ) {
                    width += col._column.width;
                }
            }
            if (width + column._column.getWidth() > this.width - 10) {
                column._column.setWidth(this.width - 11 - width);
            }
        });
        this.table.on("rowClick", (e, row) => {
            const index = row.getIndex();
            this._trendControl.selectPen(index);
        });
        this.table.on("cellClick", (e, cell) => {
            switch (cell.getField()) {
                case "graph":
                    this.setGraph(e, cell.getRow());
                    break;
                default:
                    break;
            }
        })
        this.table.on("cellDblClick", (e, cell) => {
            switch (cell.getField()) {
                case "scalemax":
                    this.setScaleMax(e, cell.getRow());
                    break;
                case "scalemin":
                    this.setScaleMin(e, cell.getRow());
                    break;
                default:
                    break;
            }
        })
        this.table.on("tableBuilt", (data) => {
            this.tableBody = this.shadowRoot.querySelector('.tabulator-table')
            this.tableHeader = this.shadowRoot.querySelector('.tabulator-header')
            this.tableBody = this.shadowRoot.querySelector('.tabulator-table')
            this._shAll = this._main.querySelector(".tabulator-col");
            const showclolumns = [{name: "showpen", nameColumn: "graph"}, {name: "showname", nameColumn: "name"}, {name: "showsource", nameColumn: "source"},
                {name: "showgroup", nameColumn: "group"}, {name: "showvalue", nameColumn: "value"}, {name: "showavarage", nameColumn: "avarage"},
                {name: "showcomment", nameColumn: "comment"}, {name: "showunit", nameColumn: "units"},  {name: "showlastvaluetime", nameColumn: "lasttime"},
                {name: "showquality", nameColumn: "isbad"}, {name: "showmin", nameColumn: "scalemin"}, {name: "showmax", nameColumn: "scalemax"},
                {name: "shownau", nameColumn: "lolo"}, {name: "shownpu", nameColumn: "lo"}, {name: "showvpu", nameColumn: "hi"},
                {name: "showvau", nameColumn: "hihi"}, {name: "showstartvalue", nameColumn: "start"}, {name: "showminvalue", nameColumn: "min"},
                {name: "showmaxvalue", nameColumn: "max"}, {name: "showlastvalue", nameColumn: "end"}]
            this.tableInited = true
            showclolumns.forEach((item) => {
                const value = this[`_initial${item.name}`];
                if (value === false) this.table.hideColumn(item.nameColumn)
                else this.table.showColumn(item.nameColumn)
            })
            this.initMenu()
            if(this.columnfontcolor) this.columnfontcolor = this._columnfontcolor;
            if(this.setcolumnitalic) this.setcolumnitalic = this._setcolumnitalic;
            if(this.setcolumnbolt) this.setcolumnbolt = this._setcolumnbolt;
            if(this.setcolumnunderline) this.setcolumnunderline = this._setcolumnunderline;
                // columnfontsize columnalignhorizontal
            if(this.columnalignhorizontal) this.columnalignhorizontal = this._columnalignhorizontal;
            if(this.columnfontsize) this.columnfontsize = this._columnfontsize;

        });
        this.connectedTimeout = setTimeout(() => {
            this.table?.redraw(true);
        }, 100);
    }

    disconnectedCallback() {
        this._saveState();
        delete this.__reciver;
        this.table?.destroy();
        this.menu?.destroy();
        this.menu = null;
        clearTimeout(this.connectedTimeout);
        delete this.table;

        super.disconnectedCallback();
    }

    _saveState() {
        const save = [];
        if (this.table && this.table.getColumnLayout) {
            const col = this.table.getColumns();
            col.forEach((el) => {
                save.push({ width: el._column.width });
            });
            let headers = Number(
                /.\d/.exec(
                    this.table.element.querySelector('[role="columnheader"]')
                        .style.height
                )
            );
            const s = { col: save, header: headers };
            this.saveItemState({ colState: s });
        }
    }
    _loadState() {
        try {
            this.originPath = this.getOriginPath();
            let storedState = $ss.getItemState(this.originPath);
            if (storedState) {
                const save = storedState.colState || [];
                for (let i = 0; i < save.col.length; i++) {
                    this.columns[i].width = save.col[i].width;
                }
                this.stateLoaded = true
            }
        } catch (error) { }
    }

    // columns state
    get showpen() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "graph")
        return elem._column.visible
    }

    set showpen(v) {
        if(!this.tableInited){
            this._initialshowpen = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("graph")
        else this.table.showColumn("graph")
        this.menu.update()
    }

    get showname() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "name")
        return elem._column.visible
    }

    set showname(v) {
        if(!this.tableInited){
            this._initialshowname = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("name")
        else this.table.showColumn("name")
        this.menu.update()
    }

    get showsource() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "source")
        return elem._column.visible
    }

    set showsource(v) {
        if(!this.tableInited){
            this._initialshowsource = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("source")
        else this.table.showColumn("source")
        this.menu.update()
    }

    get showgroup() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "group")
        return elem._column.visible
    }

    set showgroup(v) {
        if(!this.tableInited){
            this._initialshowgroup = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("group")
        else this.table.showColumn("group")
        this.menu.update()
    }

    get showvalue() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "value")
        return elem._column.visible
    }

    set showvalue(v) {
        if(!this.tableInited){
            this._initialshowvalue = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("value")
        else this.table.showColumn("value")
        this.menu.update()
    }

    get showavarage() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "avarage")
        return elem._column.visible
    }

    set showavarage(v) {
        if(!this.tableInited){
            this._initialshowavarage = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("avarage")
        else this.table.showColumn("avarage")
        this.menu.update()
    }

    get showcomment() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "comment")
        return elem._column.visible
    }

    set showcomment(v) {
        if(!this.tableInited){
            this._initialshowcomment = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("comment")
        else this.table.showColumn("comment")
        this.menu.update()
    }

    get showunit() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "units")
        return elem._column.visible
    }

    set showunit(v) {
        if(!this.tableInited){
            this._initialshowunit = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("units")
        else this.table.showColumn("units")
        this.menu.update()
    }

    get showlastvaluetime() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "lasttime")
        return elem._column.visible
    }

    set showlastvaluetime(v) {
        if(!this.tableInited){
            this._initialshowlastvaluetime = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("lasttime")
        else this.table.showColumn("lasttime")
        this.menu.update()
    }

    get showquality() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "isbad")
        return elem._column.visible
    }

    set showquality(v) {
        if(!this.tableInited){
            this._initialshowquality = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("isbad")
        else this.table.showColumn("isbad")
        this.menu.update()
    }

    get showmin() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "scalemin")
        return elem._column.visible
    }

    set showmin(v) {
        if(!this.tableInited){
            this._initialshowmin = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("scalemin")
        else this.table.showColumn("scalemin")
        this.menu.update()
    }

    get showmax() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "scalemax")
        return elem._column.visible
    }

    set showmax(v) {
        if(!this.tableInited){
            this._initialshowmax = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("scalemax")
        else this.table.showColumn("scalemax")
        this.menu.update()
    }

    get shownau() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "lolo")
        return elem._column.visible
    }

    set shownau(v) {
        if(!this.tableInited){
            this._initialshownau = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("lolo")
        else this.table.showColumn("lolo")
        this.menu.update()
    }

    get shownpu() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "lo")
        return elem._column.visible
    }
    set shownpu(v) {
        if(!this.tableInited){
            this._initialshownpu = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("lo")
        else this.table.showColumn("lo")
        this.menu.update()
    }

    get showvpu() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "hi")
        return elem._column.visible
    }

    set showvpu(v) {
        if(!this.tableInited){
            this._initialshowvpu = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("hi")
        else this.table.showColumn("hi")
        this.menu.update()
    }

    get showvau() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "hihi")
        return elem._column.visible
    }
    set showvau(v) {
        if(!this.tableInited){
            this._initialshowvau = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("hihi")
        else this.table.showColumn("hihi")
        this.menu.update()
    }

    get showstartvalue() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "start")
        return elem._column.visible
    }

    set showstartvalue(v) {
        if(!this.tableInited){
            this._initialshowstartvalue = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("start")
        else this.table.showColumn("start")
        this.menu.update()
    }

    get showminvalue() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "min")
        return elem._column.visible
    }

    set showminvalue(v) {
        if(!this.tableInited){
            this._initialshowminvalue = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("min")
        else this.table.showColumn("min")
        this.menu.update()
    }

    get showmaxvalue() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "max")
        return elem._column.visible
    }
    set showmaxvalue(v) {
        if(!this.tableInited){
            this._initialshowmaxvalue = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("max")
        else this.table.showColumn("max")
        this.menu.update()
    }

    get showlastvalue() {
        const elem = this.table.getColumns()
            .find(col => col._column.field === "end")
        return elem._column.visible
    }

    set showlastvalue(v) {
        if(!this.tableInited){
            this._initialshowlastvalue = this._toBool(v)
            return
        }
        const value = this._toBool(v)
        if (!value) this.table.hideColumn("end")
        else this.table.showColumn("end")
        this.menu.update()
    }
    // todo
    get fixcolumnwidth() {
        return this._fixcolumnwidth
    }

    set fixcolumnwidth(v) {
        const value = this._toBool(v)
        this._fixcolumnwidth = value
    }

    get columnalignvertical() {
        return this._columnalignvertical
    }

    set columnalignvertical(v) {
        this._columnalignvertical = v
        // const columns = this.table.getColumns()
        // if (v === '0') {
        //     columns.forEach((col, i) => i && col.updateDefinition({vertAlign: "top"}))
        // }
        // if (v === '2') {
        //     columns.forEach((col, i) => i && col.updateDefinition({vertAlign: "middle"}))
        // }
        // if (v === "1") {
        //     columns.forEach((col, i) => i && col.updateDefinition({vertAlign: "bottom"}))
        // }
        // this.table.redraw(true)
    }


    get columnalignhorizontal() {
        return this._columnalignhorizontal
    }
    set columnalignhorizontal(v) {
        this._columnalignhorizontal = v
        if(!this.tableInited) return
        if (v === '0') {
            const headerCols = this.shadowRoot.querySelectorAll('.tabulator-col')
            headerCols.forEach(col => col.style.textAlign = 'left')
            this.tableBody.style.textAlign = "left"
        }
        if (v === '2') {
            const headerCols = this.shadowRoot.querySelectorAll('.tabulator-col')
            headerCols.forEach(col => col.style.textAlign = 'right')
            this.tableBody.style.textAlign = "right"
        }
        if (v === "1") {
            const headerCols = this.shadowRoot.querySelectorAll('.tabulator-col')
            headerCols.forEach(col => col.style.textAlign = 'center')
            this.tableBody.style.textAlign = "center"
        }
    }

    get columnwidth() {
        return this._columnwidth
    }

    set columnwidth(v) {
        const value = parseInt(v)
        this._columnwidth = value
        if(!this.stateLoaded){
            this.columns.forEach((col) => {
                col.width = value
            })
        }
    }

    get columnfontsize() {
        return this._columnfontsize
    }

    set columnfontsize(v) {
        this._columnfontsize = v
        if(!this.tableInited) return
        this.tableBody.style.fontSize = v + "px"
        this.tableHeader.style.fontSize = v + "px"
    }

    get columnfontcolor() {
        return this._columnfontcolor
    }

    set columnfontcolor(v) {
        this._columnfontcolor = v
        if(!this.tableInited) return
        v = this.tryToGetUserColor(v);
        this.tableBody.style.color = v
        this.tableHeader.style.color = v
    }

    get setcolumnitalic() {
        return this._setcolumnitalic
    }

    set setcolumnitalic(v) {
        const value = this._toBool(v)
        this._setcolumnitalic = value
        if (!value || !this.tableInited) return
        this.tableBody.style.fontStyle = 'italic'
        this.tableHeader.style.fontStyle = 'italic'
    }

    get setcolumnbolt() {
        return this._setcolumnbolt
    }

    set setcolumnbolt(v) {
        const value = this._toBool(v)
        this._setcolumnbolt = value
        if (!value || !this.tableInited) return
        this.tableHeader.style.fontWeight = 700
        this.tableBody.style.fontWeight = 700
    }

    get setcolumnunderline() {
        return this._setcolumnunderline
    }

    set setcolumnunderline(v) {
        const value = this._toBool(v)
        this._setcolumnunderline = value
        if (!value || !this.tableInited) return
        this.tableHeader.style.textDecoration = 'underline'
        this.tableBody.style.textDecoration = 'underline'
    }
}
